import { Link } from "react-router-dom";
import { useState } from "react";

const defaultFeatures = [
  { id: "pets", label: "Allow to bring pets", price: 10, checked: false },
  {
    id: "breakfast",
    label: "Breakfast a day per person",
    price: 23,
    checked: true,
  },
  { id: "parking", label: "Parking a day", price: 5, checked: false },
  { id: "pillow", label: "Extra pillow", price: 10, checked: false },
];

const PriceDetails = ({
  basePrice = 182,
  discount = 20,
  nights = 1,
  serviceFee = 5,
  features = defaultFeatures,
  packages,
  guestsOptions = [
    "1 Adult",
    "2 Adult",
    "3 Adult",
    "1 Adult, 1 Child",
    "2 Adult, 1 Child",
    "3 Adult, 1 Child",
  ],
  hotelId = 1,
}) => {
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [guests, setGuests] = useState(guestsOptions[0]);
  const [selectedFeatures, setSelectedFeatures] = useState(
    features.reduce((acc, f) => ({ ...acc, [f.id]: f.checked }), {}),
  );
  const [selectedPackage, setSelectedPackage] = useState(null);

  const discountedPrice = basePrice - (basePrice * discount) / 100;
  const featuresTotal = features.reduce(
    (sum, f) => (selectedFeatures[f.id] ? sum + f.price : sum),
    0,
  );
  const total = discountedPrice * nights + featuresTotal + serviceFee;

  const handleFeatureChange = (id) => {
    setSelectedFeatures((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handlePackageSelection = (packageId) => {
    setSelectedPackage(packageId);
  };

  // If packages exist, render package cards instead of hotel pricing
  if (packages && packages.length > 0) {
    return (
      <div className="py-2">
        <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-2">
          Choose Your Package
        </h2>
        <div className="flex flex-col gap-1.5">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              onClick={() => handlePackageSelection(pkg.id)}
              className={`flex items-center gap-3 rounded-lg border px-3 py-1.5 cursor-pointer transition-all ${
                selectedPackage === pkg.id
                  ? "border-sky-500 bg-sky-50 ring-1 ring-sky-200"
                  : "border-gray-200 bg-white hover:border-sky-300 hover:bg-gray-50"
              }`}
            >
              {/* Radio dot */}
              <div
                className={`flex-shrink-0 w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center transition-colors ${
                  selectedPackage === pkg.id
                    ? "border-sky-500"
                    : "border-gray-300"
                }`}
              >
                {selectedPackage === pkg.id && (
                  <div className="w-1.5 h-1.5 rounded-full bg-sky-500" />
                )}
              </div>

              {/* Main info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-xs font-semibold text-gray-900 truncate">
                    {pkg.name}
                  </span>
                  {pkg.badge && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 font-medium whitespace-nowrap leading-none">
                      {pkg.badge}
                    </span>
                  )}
                  <span className="text-[10px] text-gray-400">
                    ⏱ {pkg.duration}
                  </span>
                  {pkg.group && (
                    <span className="text-[10px] text-gray-400">
                      · 👥 {pkg.group}
                    </span>
                  )}
                </div>
              </div>

              {/* Price */}
              <div className="flex-shrink-0 text-right">
                {pkg.price_per_person && (
                  <>
                    <p className="text-sm font-bold text-gray-900">
                      €{pkg.price_per_person}
                    </p>
                    <p className="text-[10px] text-gray-400 leading-none">
                      / person
                    </p>
                  </>
                )}
                {pkg.price_per_group && (
                  <>
                    <p className="text-sm font-bold text-gray-900">
                      €{pkg.price_per_group}
                    </p>
                    <p className="text-[10px] text-gray-400 leading-none">
                      / group
                    </p>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {selectedPackage && (
          <div className="mt-2.5 px-3 py-2 bg-sky-50 border border-sky-200 rounded-lg flex items-center justify-between gap-3">
            <p className="text-xs text-sky-800 font-medium truncate">
              ✓ {packages.find((p) => p.id === selectedPackage)?.name}
            </p>
            <Link
              to="/contact"
              className="flex-shrink-0 px-3 py-1.5 bg-sky-600 text-white text-xs font-semibold rounded-md hover:bg-sky-700 transition"
            >
              Book Now
            </Link>
          </div>
        )}
      </div>
    );
  }

  // Default hotel pricing if no packages
  return (
    <div className="rounded-lg bg-white border dark:bg-card-dark dark:border-dark p-4">
      <div className="flex-center-between pb-2 border-b dark:border-b-dark">
        <h1>
          <span className="text-4xl md:text-5xl font-bold">
            ${discountedPrice}
          </span>{" "}
          <span className="opacity-80 text-sm">/night</span>{" "}
          <span className="line-through opacity-80 text-sm">${basePrice}</span>
        </h1>
        <div className="flex-shrink-0">
          <span className="px-1 py-[2px] text-sm text-white bg-primary rounded-full">
            {discount}% OFF
          </span>
        </div>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-3">
        <div>
          <p>Check-In</p>
          <input
            type="date"
            className="border-none outline-none rounded-md bg-slate-100 dark:bg-hover-color-dark px-3 py-1 w-full"
            value={checkIn}
            onChange={(e) => setCheckIn(e.target.value)}
          />
        </div>
        <div>
          <p>Check-Out</p>
          <input
            type="date"
            className="border-none  outline-none rounded-md bg-slate-100 dark:bg-hover-color-dark px-3 py-1 w-full"
            value={checkOut}
            onChange={(e) => setCheckOut(e.target.value)}
          />
        </div>
      </div>
      <div className="mt-3">
        <p>Guest</p>
        <select
          className="border-none outline-none w-full rounded-md bg-slate-100 dark:bg-hover-color-dark px-3 py-2"
          value={guests}
          onChange={(e) => setGuests(e.target.value)}
        >
          {guestsOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
      <div className="mt-3">
        <h1 className="font-semibold">Extra Features</h1>
        <div className="mt-2">
          {features.map((f) => (
            <div className="flex-center-between mt-2" key={f.id}>
              <div className="input-check">
                <input
                  type="checkbox"
                  id={f.id}
                  checked={!!selectedFeatures[f.id]}
                  onChange={() => handleFeatureChange(f.id)}
                />
                <label htmlFor={f.id}>{f.label}</label>
              </div>
              <p>${f.price}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-3">
        <h1 className="font-semibold">Price</h1>
        <div className="rounded-lg p-3 bg-slate-100 dark:bg-hover-color-dark">
          <div className="flex-center-between">
            <h1>
              {nights} Night{nights > 1 ? "s" : ""}
            </h1>
            <p>${basePrice * nights}</p>
          </div>
          <div className="flex-center-between mt-2">
            <h1>Discount {discount}%</h1>
            <p>-${(basePrice * discount * nights) / 100}</p>
          </div>
          {features
            .filter((f) => selectedFeatures[f.id])
            .map((f) => (
              <div className="flex-center-between mt-2" key={f.id + "-summary"}>
                <h1>{f.label}</h1>
                <p>${f.price}</p>
              </div>
            ))}
          <div className="flex-center-between mt-2">
            <h1>Service fee</h1>
            <p>${serviceFee}</p>
          </div>
        </div>
        <div className="flex-center-between mt-5">
          <h1>Total Payment</h1>
          <h1 className="text-xl font-bold">${total}</h1>
        </div>
        <Link
          to={`/hotels/${hotelId}/confirm-booking`}
          className="btn btn-primary block text-center w-full mt-4 !opacity-100"
        >
          book now
        </Link>
      </div>
    </div>
  );
};

export default PriceDetails;
